import { HttpErrorResponse } from '@angular/common/http';

import type { ApiMetricData } from './appointment-metric.service';

/**
 * Extracts structured metric data from API request/response objects.
 * Pure functions with no Angular dependencies (except HttpErrorResponse type check).
 */
export function extractApiMetricData(
  apiData: ApiMetricData
): Record<string, unknown> {
  const metrics: Record<string, unknown> = {};

  extractBasicApiMetrics(apiData, metrics);
  extractErrorMetrics(apiData, metrics);
  extractRequestResponseData(apiData, metrics);

  return metrics;
}

export function toPascalCase(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const pascalObj: Record<string, unknown> = {};

  Object.keys(obj).forEach((key) => {
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    pascalObj[pascalKey] = obj[key];
  });

  return pascalObj;
}

function extractBasicApiMetrics(
  apiData: ApiMetricData,
  metrics: Record<string, unknown>
): void {
  if (apiData.endpoint) {
    metrics['ApiEndpoint'] = apiData.endpoint;
  }

  if (apiData.method) {
    metrics['HttpMethod'] = apiData.method;
  }

  if (apiData.statusCode) {
    metrics['HttpStatusCode'] = apiData.statusCode;
    const HTTP_SUCCESS_MIN = 200;
    const HTTP_SUCCESS_MAX = 300;
    metrics['IsSuccess'] =
      apiData.statusCode >= HTTP_SUCCESS_MIN &&
      apiData.statusCode < HTTP_SUCCESS_MAX;
  }

  if (apiData.duration) {
    metrics['ApiDuration'] = apiData.duration;
  }
}

function extractErrorMetrics(
  apiData: ApiMetricData,
  metrics: Record<string, unknown>
): void {
  if (apiData.errorData) {
    metrics['HasError'] = true;
    metrics['ErrorType'] = getErrorType(apiData.errorData);

    if (apiData.errorData instanceof HttpErrorResponse) {
      metrics['ErrorStatus'] = apiData.errorData.status;
      metrics['ErrorStatusText'] = apiData.errorData.statusText;
    }
  }
}

function extractRequestResponseData(
  apiData: ApiMetricData,
  metrics: Record<string, unknown>
): void {
  if (apiData.requestData) {
    const requestMetrics = extractRequestData(apiData.requestData);
    Object.assign(metrics, requestMetrics);
  }

  if (apiData.responseData && !apiData.errorData) {
    const responseMetrics = extractResponseData(apiData.responseData);
    Object.assign(metrics, responseMetrics);
  }
}

function extractRequestData(requestData: unknown): Record<string, unknown> {
  const data: Record<string, unknown> = {};

  if (typeof requestData === 'object' && requestData !== null) {
    const requestObj = requestData as Record<string, unknown>;

    extractBasicRequestFields(requestObj, data);
    extractPatientData(requestObj, data);
  }

  return data;
}

function extractBasicRequestFields(
  requestObj: Record<string, unknown>,
  data: Record<string, unknown>
): void {
  if ('serviceType' in requestObj)
    data['ServiceType'] = requestObj['serviceType'];
  if ('serviceId' in requestObj) data['ServiceId'] = requestObj['serviceId'];
  if ('date' in requestObj) data['Date'] = requestObj['date'];
  if ('locatorId' in requestObj) data['LocatorId'] = requestObj['locatorId'];
  if ('apptDateTime' in requestObj)
    data['ApptDateTime'] = requestObj['apptDateTime'];
  if ('confirmationCode' in requestObj)
    data['ConfirmationCode'] = requestObj['confirmationCode'];
  if ('paidBy' in requestObj) data['PaidBy'] = requestObj['paidBy'];
}

function extractPatientData(
  requestObj: Record<string, unknown>,
  data: Record<string, unknown>
): void {
  if (
    !('patient' in requestObj) ||
    typeof requestObj['patient'] !== 'object' ||
    requestObj['patient'] === null
  ) {
    return;
  }

  const patient = requestObj['patient'] as Record<string, unknown>;

  if ('encryptedLpid' in patient) {
    data['EncryptedLPID'] = patient['encryptedLpid'];
  }

  extractPatientPersonalInfo(patient, data);
}

function extractPatientPersonalInfo(
  patient: Record<string, unknown>,
  data: Record<string, unknown>
): void {
  if ('firstName' in patient && typeof patient['firstName'] === 'string') {
    data['FirstName'] = patient['firstName'];
  }
  if ('lastName' in patient && typeof patient['lastName'] === 'string') {
    data['LastName'] = patient['lastName'];
  }
  if ('email' in patient && typeof patient['email'] === 'string') {
    data['Email'] = patient['email'];
  }
  if ('phoneNumber' in patient && typeof patient['phoneNumber'] === 'string') {
    data['PhoneNumber'] = patient['phoneNumber'];
  }
  if ('consentToContact' in patient) {
    data['ConsentToContact'] = patient['consentToContact'];
  }
}

function extractResponseData(responseData: unknown): Record<string, unknown> {
  const data: Record<string, unknown> = {};

  if (typeof responseData === 'object' && responseData !== null) {
    const responseObj = responseData as Record<string, unknown>;

    if ('results' in responseObj && Array.isArray(responseObj['results'])) {
      data['ResultCount'] = responseObj['results'].length;
    }

    if ('confirmationCode' in responseObj) {
      data['ConfirmationCode'] = responseObj['confirmationCode'];
    }

    if ('encryptedLpid' in responseObj) {
      data['EncryptedLPID'] = responseObj['encryptedLpid'];
    }
  }

  return data;
}

export function getErrorType(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return 'NetworkError';
    }
    const HTTP_CLIENT_ERROR_MIN = 400;
    const HTTP_CLIENT_ERROR_MAX = 500;
    const HTTP_SERVER_ERROR_MIN = 500;
    if (
      error.status >= HTTP_CLIENT_ERROR_MIN &&
      error.status < HTTP_CLIENT_ERROR_MAX
    ) {
      return 'ClientError';
    }
    if (error.status >= HTTP_SERVER_ERROR_MIN) {
      return 'ServerError';
    }
    return 'HttpError';
  }

  if (error instanceof Error) {
    return error.constructor.name;
  }

  return 'UnknownError';
}
